<script>
  import { onDestroy, onMount } from "svelte";
  import { Loading} from "carbon-components-svelte";

  import Header from "lib/components/Header.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import {
    Pagination,
    Select,
    Tooltip,
    Button,
    SaveStatus,
    SectionWrapper,
  } from "paxray-components-svelte";
  import EmptyStateText from "./components/EmptyStateText.svelte";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";

  const navigate = useNavigate();
  import Modal from "lib/components/Modal.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import { recordingStore } from "@stores/recordingStore.js";
  import LFilters from "./components/LFilters.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import { TRASH_ICON } from '@utils/icons.js';

  let idproject = new URLSearchParams(window.location.search).get("idproject");
  let rows = [];
  let frequencies = {};
  let rowValues = {};
  let fieldSources = {};
  let systemValues = {};
  let surveyValues = {};
  let loading = true;
  let saving = false;
  let tableModeIndex = 0;
  $: tableMode = tableModeIndex === 0 ? "hard" : "soft";
  let costCurrency = "EUR";
  let selectedTeam = "";
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTask = "";
  let selectedSubtask = "";
  let searchTerm = "";
  let page = 1;
  let pageSize = 10;
  let sortColumn = "l4";
  let sortDirection = "asc";
  let proposalModalRow = null;
  let proposalModalMode = "";
  let proposalModalIndex = null;
  let proposalModalStartIndex = null;
  let deleteProposalTarget = null;
  let deleteProposalModalOpen = false;
  let saveTimeout = null;
  let expandedProposalTexts = {};
  let hasUnsavedChanges = false;
  let lastCostCurrency = costCurrency;
  let taskFeedbackModalOpen = false;
  let taskFeedbackStep = 1;
  let taskFeedbackSetup = {};
  let taskFeedbackName = "Employee task feedback";
  let selectedFeedbackTaskIds = [];
  let selectedFeedbackKpis = [];
  let feedbackCommentEnabled = true;
  let savingTaskFeedback = false;
  let sendingTaskFeedback = false;
  let taskFeedbackSendModalOpen = false;
  let taskFeedbackError = "";
  let taskFeedbackNotice = "";
  let taskFeedbackPreviewOpen = false;
  let previewTaskIndex = 0;
  let previewSkipped = false;
  let previewValues = {};
  let previewIdeas = [{ text: "", topics: [], timeShare: "", occurrence: "" }];
  let previewError = "";
  const feedbackDateValue = (daysFromToday) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    return date.toISOString().slice(0, 10);
  };
  let taskFeedbackSettings = {
    start_date: feedbackDateValue(1),
    end_date: feedbackDateValue(7),
    reminder_days: 1,
    reminder2_days: 2,
    language: "en",
    email_signature: "",
    contact_person: "",
    contact_person_email: "",
  };
  let enableFirstFeedbackReminder = true;
  let enableSecondFeedbackReminder = true;
  let includeFeedbackContact = false;
  const currencyOptions = [
    { code: "EUR", symbol: "\u20AC" },
    { code: "USD", symbol: "$" },
    { code: "JPY", symbol: "\u00A5" },
    { code: "GBP", symbol: "\u00A3" },
    { code: "CHF", symbol: "CHF" },
  ];
  const currencySelectOptions = currencyOptions.map((option) => ({
    value: option.code,
    text: option.code,
  }));
  const percentageOptions = [
    { value: "", label: "Not set" },
    ...Array.from({ length: 11 }, (_, index) => ({
      value: String(index * 10),
      label: `${index * 10}%`,
    })),
  ];
  const sourceOptions = ["system", "human", "survey"];
  const systemFields = new Set(["active_users", "avg_case_time_seconds"]);
  const editableTextFields = new Set([
    "active_users",
    "avg_case_time_seconds",
    "monthly_frequency",
    "cost_per_hour",
  ]);
  const percentageFields = new Set([
    "standard_case_share",
    "rework_share",
    "communication_outside_system_share",
  ]);
  const softSelectFields = new Set([
    "pain_level",
    "business_criticality",
    "input_standardization",
    "perceived_value",
  ]);
  const hardHeaders = [
    { key: "l4", value: "Task" },
    { key: "active_users", value: "Employees (#)" },
    { key: "monthly_frequency", value: "Cases/year (#)" },
    { key: "avg_case_time_seconds", value: "Avg. time/case (min)" },
    { key: "cost_per_hour", value: "Cost/hour (€)" },
    {
      key: "standard_case_share",
      value: "Standard cases",
      tooltip:
        "Share of cases that follow the standard process without relevant deviations.",
    },
    {
      key: "rework_share",
      value: "Rework share",
      tooltip:
        "Estimated share of effort caused by corrections, repetitions, or fixing errors.",
    },
    {
      key: "communication_outside_system_share",
      value: "Offline comms",
      tooltip: "Estimated share of communication handled outside IT systems.",
    },
  ];
  const softHeaders = [
    { key: "l4", value: "Task" },
    {
      key: "pain_level",
      value: "Pain level",
      tooltip:
        "Subjective rating of how painful or frustrating the task is for users.",
    },
    {
      key: "business_criticality",
      value: "Business criticality",
      tooltip:
        "Subjective rating of how important this task is for business operations.",
    },
    {
      key: "input_standardization",
      value: "Input standardization",
      tooltip:
        "Subjective rating of how structured and consistent the required inputs are.",
    },
    {
      key: "perceived_value",
      value: "Perceived value",
      tooltip:
        "Subjective rating of how valuable the task output is perceived to be.",
    },
    {
      key: "improvement_proposals",
      value: "Improvement ideas",
      tooltip: "Add or review task-specific improvement ideas and pain points.",
    },
  ];
  const improvementTopics = [
    "Unnecessary effort",
    "Many system switches",
    "Errors / rework",
    "System waiting time",
    "Waiting for colleagues",
    "Manual workaround",
    "Clarification needed",
  ];
  const surveyImprovementTopics = [
    "Unnecessary effort",
    "Errors / rework",
    "System waiting time",
    "Waiting for colleagues",
    "Manual workaround",
  ];
  const timeShareOptions = ["<10%", "10-25%", "25-50%", ">50%"];
  const occurrenceOptions = ["Rarely", "Sometimes", "Often", "Almost always"];
  const timeShareScores = {
    "<10%": 5,
    "10-25%": 17.5,
    "25-50%": 37.5,
    ">50%": 60,
  };
  const occurrenceScores = {
    Rarely: 1,
    Sometimes: 2,
    Often: 3,
    "Almost always": 4,
  };
  const taskFeedbackKpiGroups = [
    {
      title: "Hard facts",
      description: "Operational estimates for the selected tasks.",
      kpis: [
        {
          id: "monthly_frequency",
          title: "Cases/year",
          description: "How often is the task performed?",
        },
        {
          id: "avg_case_time_seconds",
          title: "Avg. time/case",
          description: "How long does a typical case take?",
        },
        {
          id: "standard_case_share",
          title: "Standard cases",
          description: "How often does the standard process apply?",
        },
        {
          id: "rework_share",
          title: "Rework share",
          description: "How often is correction or repetition needed?",
        },
        {
          id: "communication_outside_system_share",
          title: "Offline comms",
          description: "How much coordination happens outside the system?",
        },
      ],
    },
    {
      title: "Soft facts",
      description: "Perceived quality and improvement potential.",
      kpis: [
        {
          id: "pain_level",
          title: "Pain level",
          description: "How frustrating or effort-intensive is the task?",
        },
        {
          id: "business_criticality",
          title: "Business criticality",
          description: "How important is it for daily operations?",
        },
        {
          id: "input_standardization",
          title: "Input standardization",
          description: "How structured and consistent are the inputs?",
        },
        {
          id: "perceived_value",
          title: "Perceived value",
          description: "How valuable is the task outcome?",
        },
      ],
    },
  ];
  const taskFeedbackKpiIds = new Set(
    taskFeedbackKpiGroups.flatMap((group) => group.kpis.map((kpi) => kpi.id)),
  );

  $: rowsForSelectedTeam = rows.filter((row) =>
    rowMatchesTeam(row, selectedTeam),
  );
  $: selectedProcessName = selectedProcess
    ? ($recordingStore.processes?.find(
        (process) => process.id === selectedProcess,
      )?.name ?? null)
    : null;
  $: selectedSubprocessName = selectedSubprocess
    ? ($recordingStore.listOfSubprocesses?.find(
        (subprocess) => subprocess.id === selectedSubprocess,
      )?.name ?? null)
    : null;
  $: selectedTaskName = selectedTask
    ? ($recordingStore.listOfTasks?.find((task) => task.id === selectedTask)
        ?.name ?? null)
    : null;
  $: selectedSubtaskName = selectedSubtask
    ? ($recordingStore.listOfSubtasks?.find(
        (subtask) => subtask.id === selectedSubtask,
      )?.name ?? null)
    : null;
  $: filteredRows = rowsForSelectedTeam.filter(
    (row) =>
      (!selectedProcessName || row.l1 === selectedProcessName) &&
      (!selectedSubprocessName || row.l2 === selectedSubprocessName) &&
      (!selectedTaskName || row.l3 === selectedTaskName) &&
      (!selectedSubtaskName || row.l4 === selectedSubtaskName) &&
      rowMatchesSearch(row, searchTerm),
  );
  $: sortedRows = sortRows(filteredRows, sortColumn, sortDirection);
  $: pageCount = Math.max(Math.ceil(sortedRows.length / pageSize), 1);
  $: if (page > pageCount) page = pageCount;
  $: tableHeaders =
    tableMode === "hard"
      ? hardHeaders.map((header) =>
          header.key === "cost_per_hour"
            ? {
                ...header,
                value: `Cost/h (${getCurrencySymbol(costCurrency)})`,
              }
            : header,
        )
      : softHeaders;
  $: modalProposals =
    proposalModalRow && rowValues
      ? getRawImprovementPotentials(proposalModalRow)
      : [];
  $: modalProposalEntries =
    proposalModalMode === "add" && proposalModalIndex !== null
      ? modalProposals
          .map((proposal, index) => ({ proposal, index }))
          .filter((entry) => entry.index >= proposalModalStartIndex)
      : modalProposals
          .map((proposal, index) => ({ proposal, index }))
          .filter((entry) => String(entry.proposal?.text || "").trim())
          .reverse();
  $: if (costCurrency !== lastCostCurrency) {
    lastCostCurrency = costCurrency;
    scheduleSave();
  }
  $: currentModalProposal =
    proposalModalMode === "add" && modalProposalEntries.length
      ? modalProposalEntries[modalProposalEntries.length - 1].proposal
      : null;
  $: canAddAnotherProposal =
    proposalModalMode !== "add" ||
    isImprovementPotentialComplete(currentModalProposal);
  $: selectedFeedbackRows = rows.filter((row) =>
    selectedFeedbackTaskIds.includes(String(row.id)),
  );
  $: feedbackTeams = getFeedbackTeamOptions(selectedFeedbackRows).map(
    (team) => team.name,
  );
  $: previewTask = selectedFeedbackRows[previewTaskIndex];

  function getEditableInputValue(
    row,
    key,
    _values = rowValues,
    _frequencies = frequencies,
  ) {
    const value = getDisplayedValue(row, key);
    if (value === "" || value === null || value === undefined) return "";
    if (key === "avg_case_time_seconds") {
      return formatDurationAsMinutesSeconds(value);
    }
    if (key === "monthly_frequency") {
      return formatInteger(value);
    }
    if (key === "active_users") {
      return formatInteger(value);
    }
    return value;
  }

  function parseEditableInputValue(key, value) {
    if (key === "avg_case_time_seconds") return parseDurationInput(value);
    if (key === "active_users" || key === "monthly_frequency") {
      return parseNumberInput(value);
    }
    return value;
  }

  function hasSourceSelect(key) {
    return (
      editableTextFields.has(key) ||
      percentageFields.has(key) ||
      softSelectFields.has(key)
    );
  }

  function getFeedbackTeamOptions(sourceRows) {
    const teams = new Map();
    sourceRows.forEach((row) => {
      (row.teamIds || []).forEach((teamId, index) => {
        if (!teamId) return;
        const id = String(teamId);
        const team = teams.get(id) || {
          id,
          name: row.teams?.[index] || "Team",
          taskCount: 0,
        };
        team.taskCount += 1;
        teams.set(id, team);
      });
    });
    return [...teams.values()].sort((left, right) =>
      left.name.localeCompare(right.name),
    );
  }

  function openTaskFeedbackModal() {
    const setup = taskFeedbackSetup || {};
    taskFeedbackName = setup.name || "Employee task feedback";
    selectedFeedbackTaskIds = Array.isArray(setup.task_ids)
      ? setup.task_ids.map(String)
      : [];
    selectedFeedbackKpis = Array.isArray(setup.kpis)
      ? setup.kpis.filter((kpi) => taskFeedbackKpiIds.has(kpi))
      : [];
    feedbackCommentEnabled = setup.comment_enabled !== false;
    taskFeedbackSettings = {
      ...taskFeedbackSettings,
      start_date: setup.start_date || feedbackDateValue(1),
      end_date: setup.end_date || feedbackDateValue(7),
      reminder_days: setup.reminder_days ?? 1,
      reminder2_days: setup.reminder2_days ?? 2,
      language: setup.language || "en",
      email_signature: setup.email_signature || "",
      contact_person: setup.contact_person || "",
      contact_person_email: setup.contact_person_email || "",
    };
    enableFirstFeedbackReminder =
      setup.reminder_days !== null && setup.reminder_days !== undefined;
    enableSecondFeedbackReminder =
      setup.reminder2_days !== null && setup.reminder2_days !== undefined;
    includeFeedbackContact = Boolean(
      setup.contact_person || setup.contact_person_email,
    );
    taskFeedbackStep = 1;
    taskFeedbackError = "";
    taskFeedbackNotice = "";
    taskFeedbackModalOpen = true;
  }

  function toggleFeedbackTask(taskId) {
    const id = String(taskId);
    selectedFeedbackTaskIds = selectedFeedbackTaskIds.includes(id)
      ? selectedFeedbackTaskIds.filter((item) => item !== id)
      : [...selectedFeedbackTaskIds, id];
    taskFeedbackError = "";
  }

  function selectVisibleFeedbackTasks() {
    selectedFeedbackTaskIds = [
      ...new Set([
        ...selectedFeedbackTaskIds,
        ...sortedRows.map((row) => String(row.id)),
      ]),
    ];
    taskFeedbackError = "";
  }

  function toggleFeedbackKpi(kpi) {
    if (selectedFeedbackKpis.includes(kpi)) {
      selectedFeedbackKpis = selectedFeedbackKpis.filter(
        (item) => item !== kpi,
      );
    } else {
      selectedFeedbackKpis = [...selectedFeedbackKpis, kpi];
    }
    taskFeedbackError = "";
  }

  function getTaskFeedbackKpi(kpiId) {
    return taskFeedbackKpiGroups
      .flatMap((group) => group.kpis)
      .find((kpi) => kpi.id === kpiId);
  }

  function getFeedbackOptions(kpiId) {
    const optionsByKpi = {
      monthly_frequency: [
        "Less frequently",
        "Monthly",
        "Weekly",
        "Several times a week",
        "Daily",
      ],
      avg_case_time_seconds: [
        "Less than 5 minutes",
        "5–15 minutes",
        "15–60 minutes",
        "1–2 hours",
        "More than 2 hours",
      ],
      standard_case_share: ["0–10%", "10–30%", "30–70%", "70–90%", "90–100%"],
      rework_share: ["0–10%", "10–30%", "30–50%", "50–70%", "70–100%"],
      communication_outside_system_share: [
        "0–10%",
        "10–30%",
        "30–50%",
        "50–70%",
        "70–100%",
      ],
      pain_level: ["Very low", "Low", "Moderate", "High", "Very high"],
      business_criticality: ["Low", "Limited", "Moderate", "High", "Critical"],
      input_standardization: [
        "Always the same",
        "Often the same",
        "Partly the same",
        "Partly variable",
        "Very variable",
      ],
      perceived_value: ["Very low", "Low", "Moderate", "High", "Very high"],
    };
    return (optionsByKpi[kpiId] || optionsByKpi.pain_level).map(
      (label, index) => ({ value: String(index + 1), label }),
    );
  }

  function getLikertOptions(kpiId) {
    return getFeedbackOptions(kpiId).map((option) => option.label);
  }

  function getLikertSelectOptions(kpiId) {
    return [{ value: "", label: "Not set" }, ...getFeedbackOptions(kpiId)];
  }

  function openTaskFeedbackPreview() {
    previewTaskIndex = 0;
    previewSkipped = false;
    previewValues = {};
    previewIdeas = [createPreviewIdea()];
    previewError = "";
    taskFeedbackPreviewOpen = true;
  }

  function nextPreviewTask() {
    if (!previewSkipped) {
      const unansweredKpis = selectedFeedbackKpis.filter(
        (kpiId) => !previewValues[kpiId],
      );
      if (unansweredKpis.length) {
        const labels = unansweredKpis
          .map(
            (kpiId) =>
              getTaskFeedbackKpi(kpiId)?.title || "a selected question",
          )
          .join(", ");
        previewError = `Please answer: ${labels} — or skip this task.`;
        return;
      }
      if (
        previewIdeas.some(
          (idea) => isPreviewIdeaStarted(idea) && !isPreviewIdeaComplete(idea),
        )
      ) {
        previewError =
          "Complete the improvement idea or clear it before continuing.";
        return;
      }
    }
    if (previewTaskIndex >= selectedFeedbackRows.length - 1) {
      taskFeedbackPreviewOpen = false;
      return;
    }
    previewTaskIndex += 1;
    previewSkipped = false;
    previewValues = {};
    previewIdeas = [createPreviewIdea()];
    previewError = "";
  }

  function createPreviewIdea() {
    return { text: "", topics: [], timeShare: "", occurrence: "" };
  }

  function isPreviewIdeaComplete(idea) {
    return Boolean(
      idea?.text?.trim() &&
        idea?.topics?.length &&
        idea?.timeShare &&
        idea?.occurrence,
    );
  }

  function isPreviewIdeaStarted(idea) {
    return Boolean(
      idea?.text?.trim() ||
        idea?.topics?.length ||
        idea?.timeShare ||
        idea?.occurrence,
    );
  }

  function addPreviewIdea() {
    const latestIdea = previewIdeas[previewIdeas.length - 1];
    if (latestIdea && !isPreviewIdeaComplete(latestIdea)) return;
    previewIdeas = [...previewIdeas, createPreviewIdea()];
  }

  function updatePreviewIdea(index, changes) {
    previewIdeas = previewIdeas.map((idea, ideaIndex) =>
      ideaIndex === index ? { ...idea, ...changes } : idea,
    );
    previewError = "";
  }

  function togglePreviewIdeaTopic(index, topic) {
    const idea = previewIdeas[index];
    const topics = idea.topics.includes(topic)
      ? idea.topics.filter((item) => item !== topic)
      : [...idea.topics, topic];
    updatePreviewIdea(index, { topics });
  }

  function togglePreviewIdeaValue(index, field, value) {
    updatePreviewIdea(index, {
      [field]: previewIdeas[index][field] === value ? "" : value,
    });
  }

  function removePreviewIdea(index) {
    previewIdeas =
      index === 0
        ? [createPreviewIdea(), ...previewIdeas.slice(1)]
        : previewIdeas.filter((_, ideaIndex) => ideaIndex !== index);
  }

  function continueTaskFeedbackSetup() {
    if (!selectedFeedbackTaskIds.length) {
      taskFeedbackError = "Select at least one task.";
      return;
    }
    taskFeedbackStep = 2;
    taskFeedbackError = "";
  }

  function continueTaskFeedbackDelivery() {
    if (!selectedFeedbackKpis.length) {
      taskFeedbackError = "Select at least one KPI.";
      return;
    }
    taskFeedbackStep = 3;
    taskFeedbackError = "";
  }

  function getTaskFeedbackPayload() {
    return {
      source_project_id: idproject,
      project_id: idproject,
      name: taskFeedbackName,
      task_ids: selectedFeedbackTaskIds,
      tasks: selectedFeedbackRows.map((row) => ({
        id: String(row.id),
        name: row.l4,
        hierarchy: [row.l1, row.l2, row.l3].filter(Boolean),
        teams: row.teams || [],
      })),
      kpis: selectedFeedbackKpis,
      comment_enabled: feedbackCommentEnabled,
      ...taskFeedbackSettings,
      reminder_days: enableFirstFeedbackReminder
        ? taskFeedbackSettings.reminder_days
        : null,
      reminder2_days: enableSecondFeedbackReminder
        ? taskFeedbackSettings.reminder2_days
        : null,
      contact_person: includeFeedbackContact
        ? taskFeedbackSettings.contact_person
        : "",
      contact_person_email: includeFeedbackContact
        ? taskFeedbackSettings.contact_person_email
        : "",
    };
  }

  async function persistTaskFeedbackSetup(closeAfterSave = true) {
    if (!taskFeedbackSettings.start_date || !taskFeedbackSettings.end_date) {
      taskFeedbackError = "Select a start and end date.";
      return null;
    }
    savingTaskFeedback = true;
    taskFeedbackError = "";
    taskFeedbackNotice = "";
    try {
      const payload = getTaskFeedbackPayload();
      const surveyResponse = await fetchWithSession(
        "/api/knextractor/task-feedback-surveys",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const surveyResult = await surveyResponse.json();
      if (!surveyResponse.ok || !surveyResult.success) {
        throw new Error(
          surveyResult.error || "Could not create the task feedback survey.",
        );
      }
      const response = await fetchWithSession(
        "/api/damprojects/saveTaskFeedbackSetup",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            survey_id: surveyResult.survey_id,
            test_recipient: surveyResult.recipient,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Could not save task feedback setup.",
        );
      }
      taskFeedbackSetup = result.setup;
      if (closeAfterSave) taskFeedbackModalOpen = false;
      return surveyResult;
    } catch (error) {
      taskFeedbackError =
        error.message || "Could not save task feedback setup.";
      return null;
    } finally {
      savingTaskFeedback = false;
    }
  }

  function saveTaskFeedbackSetup() {
    persistTaskFeedbackSetup(true);
  }

  async function confirmTaskFeedbackTestSend() {
    sendingTaskFeedback = true;
    taskFeedbackError = "";
    taskFeedbackNotice = "";
    const survey = await persistTaskFeedbackSetup(false);
    if (!survey) {
      sendingTaskFeedback = false;
      taskFeedbackSendModalOpen = false;
      return;
    }
    try {
      const response = await fetchWithSession(
        `/api/knextractor/task-feedback-surveys/${survey.survey_id}/send-test`,
        { method: "POST" },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Could not send the test invitation.");
      taskFeedbackNotice = result.emails_sent
        ? `Test invitation sent to ${result.recipient}.`
        : `No pending invitation for ${result.recipient}.`;
      taskFeedbackSendModalOpen = false;
    } catch (error) {
      taskFeedbackError =
        error.message || "Could not send the test invitation.";
      taskFeedbackSendModalOpen = false;
    } finally {
      sendingTaskFeedback = false;
    }
  }

  function rowMatchesTeam(row, team) {
    return (
      !team ||
      (row.teamIds || []).map((teamId) => String(teamId)).includes(String(team))
    );
  }

  function rowMatchesSearch(row, search) {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [row.l1, row.l2, row.l3, row.l4]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  }

  function getTaskHierarchyTooltip(row) {
    return [
      `E2E Process (L1): ${row.l1 || "-"}`,
      `Subprocess (L2): ${row.l2 || "-"}`,
      `Task Area (L3): ${row.l3 || "-"}`,
    ].join("\n");
  }

  function getRowValue(
    row,
    key,
    _values = rowValues,
    _frequencies = frequencies,
  ) {
    if (key === "monthly_frequency") return frequencies[row.id] ?? 100;
    return rowValues[row.id]?.[key] ?? row[key] ?? "";
  }

  function isNotRated(row, key, _dependency = rowValues) {
    const value = getDisplayedValue(row, key);
    return value === "" || value === null || value === undefined;
  }

  function getSourceValue(row, key, source) {
    if (source === "system") return systemValues[row.id]?.[key] ?? "";
    if (source === "survey") return surveyValues[row.id]?.[key] ?? "";
    return getRowValue(row, key);
  }

  function getDisplayedValue(row, key) {
    return getSourceValue(row, key, getFieldSource(row, key));
  }

  function isUserSource(row, key) {
    return getFieldSource(row, key) === "human";
  }

  function isQuestionnaireSource(row, key) {
    return getFieldSource(row, key) === "survey";
  }

  function getQuestionnaireValueLabel(row, key) {
    const value = surveyValues[row.id]?.[key];
    if (value === "" || value === null || value === undefined) {
      return "Not set";
    }
    return (
      getFeedbackOptions(key).find((option) => option.value === String(value))
        ?.label || String(value)
    );
  }

  function getFieldSource(row, key, _sources = fieldSources) {
    return (
      fieldSources[row.id]?.[key] ||
      (systemFields.has(key) ? "system" : "human")
    );
  }

  function normalizeFieldSources(sources) {
    const normalized = sources || {};
    if (normalized.avg_case_time_minutes && !normalized.avg_case_time_seconds) {
      return {
        ...normalized,
        avg_case_time_seconds: normalized.avg_case_time_minutes,
      };
    }
    return normalized;
  }

  function getOptionalPercentageValue(row, key) {
    const value = row[key];
    const sources = normalizeFieldSources(row.field_sources);
    if ((value === 0 || value === "0") && !sources[key]) return "";
    return value ?? "";
  }

  function setFieldSource(row, key, source) {
    fieldSources = {
      ...fieldSources,
      [row.id]: {
        ...(fieldSources[row.id] || {}),
        [key]: source,
      },
    };
    scheduleSave();
  }

  function sourceAvailable(_row, _key, _source) {
    return true;
  }

  function setRowValue(row, key, value) {
    if (key === "monthly_frequency") {
      frequencies = { ...frequencies, [row.id]: value };
      scheduleSave();
      return;
    }
    rowValues = {
      ...rowValues,
      [row.id]: {
        ...(rowValues[row.id] || {}),
        [key]: value,
      },
    };
    scheduleSave();
  }

  function setHumanRowValue(row, key, value) {
    setRowValue(row, key, value);
    setFieldSource(row, key, "human");
  }

  function formatInteger(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "";
    return new Intl.NumberFormat("de-DE", {
      maximumFractionDigits: 0,
    }).format(numericValue);
  }

  function parseNumberInput(value) {
    return String(value || "")
      .replace(/\./g, "")
      .replace(",", ".");
  }

  function toNumber(value) {
    return Number(parseNumberInput(value)) || 0;
  }

  function toOptionalNumber(value) {
    return value === "" || value === null || value === undefined
      ? ""
      : toNumber(value);
  }

  function formatDurationAsMinutesSeconds(value) {
    const totalSeconds = Math.round(Number(value) || 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  function parseDurationInput(value) {
    const text = String(value || "").trim();
    if (!text) return "";
    if (text.includes(":")) {
      const [minutes, seconds] = text
        .split(":")
        .map((part) => Number(part) || 0);
      return minutes * 60 + seconds;
    }
    return parseNumberInput(text);
  }

  function getCurrencySymbol(currencyCode) {
    const currency = currencyCode || "EUR";
    return (
      currencyOptions.find((option) => option.code === currency)?.symbol ||
      currency
    );
  }

  function sourceLabel(source) {
    if (source === "system") return "S";
    if (source === "survey") return "Q";
    return "U";
  }

  function sourceTitle(source) {
    if (source === "system") return "System";
    if (source === "survey") return "Questionnaire";
    return "User";
  }

  function autogrow(node) {
    const resize = () => {
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    };
    resize();
    node.addEventListener("input", resize);
    return {
      update: resize,
      destroy() {
        node.removeEventListener("input", resize);
      },
    };
  }

  function countImprovementProposals(row, _dependency = rowValues) {
    return getImprovementPotentials(row).length;
  }

  function countImprovementProposalsFrom(proposals) {
    return proposals.filter((item) => String(item?.text || "").trim()).length;
  }

  function getImprovementPotentials(row) {
    const value = getRowValue(row, "improvement_potentials");
    if (Array.isArray(value)) {
      return value.filter((item) => String(item?.text || "").trim());
    }
    const legacyValue = getRowValue(row, "improvement_proposals");
    if (!legacyValue) return [];
    if (Array.isArray(legacyValue)) return legacyValue;
    return String(legacyValue || "")
      .split(/\r?\n|;|\|/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((text) => ({
        id:
          globalThis.crypto?.randomUUID?.() ||
          `proposal_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        text,
        topics: [],
        timeShare: "",
        occurrence: "",
      }));
  }

  function getRawImprovementPotentials(row) {
    const value = getRowValue(row, "improvement_potentials");
    return Array.isArray(value) ? value : getImprovementPotentials(row);
  }

  function setImprovementPotentials(row, potentials) {
    setRowValue(row, "improvement_potentials", potentials);
  }

  function createImprovementPotential() {
    return {
      id:
        globalThis.crypto?.randomUUID?.() ||
        `proposal_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      text: "",
      topics: [],
      timeShare: "",
      occurrence: "",
    };
  }

  function addImprovementPotential(row) {
    setImprovementPotentials(row, [
      ...getRawImprovementPotentials(row),
      createImprovementPotential(),
    ]);
  }

  function appendImprovementPotential(row) {
    const potentials = getRawImprovementPotentials(row);
    setImprovementPotentials(row, [
      ...potentials,
      createImprovementPotential(),
    ]);
    return potentials.length;
  }

  function addAnotherImprovementPotential(row) {
    if (!canAddAnotherProposal) return;
    appendImprovementPotential(row);
  }

  function isImprovementPotentialComplete(proposal) {
    return (
      String(proposal?.text || "").trim().length > 0 &&
      (proposal?.topics || []).length > 0 &&
      Boolean(proposal?.timeShare) &&
      Boolean(proposal?.occurrence)
    );
  }

  function updateImprovementPotential(row, index, patch) {
    setImprovementPotentials(
      row,
      getRawImprovementPotentials(row).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    );
  }

  function getPreviewText(text) {
    const cleanText = String(text || "");
    return cleanText.length > 200 ? `${cleanText.slice(0, 200)}...` : cleanText;
  }

  function toggleProposalText(proposal) {
    if (!proposal?.id) return;
    expandedProposalTexts = {
      ...expandedProposalTexts,
      [proposal.id]: !expandedProposalTexts[proposal.id],
    };
  }

  function removeImprovementPotential(row, index) {
    const nextPotentials = getRawImprovementPotentials(row).filter(
      (_, itemIndex) => itemIndex !== index,
    );
    setImprovementPotentials(row, nextPotentials);
    if (proposalModalIndex !== null && index <= proposalModalIndex) {
      proposalModalIndex = Math.max(proposalModalIndex - 1, 0);
    }
    if (proposalModalStartIndex !== null && index < proposalModalStartIndex) {
      proposalModalStartIndex = Math.max(proposalModalStartIndex - 1, 0);
    }
    return nextPotentials.length;
  }

  function requestRemoveImprovementPotential(row, index) {
    deleteProposalTarget = { row, index };
    deleteProposalModalOpen = true;
  }

  function confirmRemoveImprovementPotential() {
    if (!deleteProposalTarget) return;
    const remainingIdeas = removeImprovementPotential(
      deleteProposalTarget.row,
      deleteProposalTarget.index,
    );
    deleteProposalTarget = null;
    deleteProposalModalOpen = false;
    if (remainingIdeas === 0) {
      closeProposalModal();
    }
  }

  function toggleImprovementTopic(row, index, topic) {
    const item = getRawImprovementPotentials(row)[index] || {};
    const topics = item.topics || [];
    updateImprovementPotential(row, index, {
      topics: topics.includes(topic)
        ? topics.filter((entry) => entry !== topic)
        : [...topics, topic],
    });
  }

  function getTopicCounts(row) {
    const counts = {};
    getImprovementPotentials(row).forEach((item) => {
      (item.topics || []).forEach((topic) => {
        counts[topic] = (counts[topic] || 0) + 1;
      });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }

  function getTopicCountsFrom(proposals) {
    const counts = {};
    proposals.forEach((item) => {
      (item.topics || []).forEach((topic) => {
        counts[topic] = (counts[topic] || 0) + 1;
      });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }

  function getAverageOption(proposals, key, scores, fallback = "-") {
    const values = proposals
      .map((proposal) => proposal[key])
      .filter((value) => scores[value]);
    if (!values.length) return fallback;
    const average =
      values.reduce((sum, value) => sum + scores[value], 0) / values.length;
    return Object.entries(scores).sort(
      (a, b) => Math.abs(a[1] - average) - Math.abs(b[1] - average),
    )[0][0];
  }

  function openProposalModal(row, mode) {
    proposalModalRow = row;
    proposalModalMode = mode;
    if (mode === "add") {
      const potentials = getRawImprovementPotentials(row);
      const hasIdeas = getImprovementPotentials(row).length > 0;
      if (!hasIdeas && potentials.length) {
        proposalModalIndex = potentials.findIndex(
          (proposal) => !String(proposal?.text || "").trim(),
        );
      } else {
        proposalModalIndex = appendImprovementPotential(row);
      }
    } else {
      proposalModalIndex = null;
    }
    proposalModalStartIndex = proposalModalIndex;
  }

  function closeProposalModal() {
    proposalModalRow = null;
    proposalModalMode = "";
    proposalModalIndex = null;
    proposalModalStartIndex = null;
  }

  function setSort(column) {
    if (sortColumn === column) {
      sortDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      sortColumn = column;
      sortDirection = "asc";
    }
    page = 1;
  }

  function sortRows(sourceRows, column, directionValue) {
    const direction = directionValue === "desc" ? -1 : 1;
    return [...sourceRows].sort((a, b) => {
      const left = getSortableValue(a, column);
      const right = getSortableValue(b, column);
      if (typeof left === "number" && typeof right === "number") {
        return (left - right) * direction;
      }
      return String(left).localeCompare(String(right)) * direction;
    });
  }

  function getSortableValue(row, column) {
    if (column === "l4") return row.l4 || "";
    const value = getRowValue(row, column);
    const numericValue = Number(parseNumberInput(value));
    return Number.isFinite(numericValue) && value !== ""
      ? numericValue
      : value || "";
  }

  async function loadTaskFeedbackResults() {
    if (!taskFeedbackSetup?.survey_id) return;
    const response = await fetchWithSession(
      `/api/knextractor/task-feedback-surveys/${taskFeedbackSetup.survey_id}/results`,
    );
    if (!response.ok) return;
    const result = await response.json();
    let changed = false;
    const mergedValues = { ...surveyValues };
    const mergedRowValues = { ...rowValues };
    Object.entries(result.tasks || {}).forEach(([taskId, taskResult]) => {
      const existing = mergedValues[taskId] || {};
      const next = { ...existing, ...(taskResult.values || {}) };
      if (JSON.stringify(existing) !== JSON.stringify(next)) changed = true;
      mergedValues[taskId] = next;
      const existingIdeas =
        mergedRowValues[taskId]?.improvement_potentials || [];
      const nextIdeas = [
        ...existingIdeas.filter((idea) => idea.source !== "survey"),
        ...(taskResult.ideas || []),
      ];
      if (JSON.stringify(existingIdeas) !== JSON.stringify(nextIdeas))
        changed = true;
      mergedRowValues[taskId] = {
        ...(mergedRowValues[taskId] || {}),
        improvement_potentials: nextIdeas,
      };
    });
    if (!changed) return;
    surveyValues = mergedValues;
    rowValues = mergedRowValues;
    await saveRows();
  }

  async function loadRows() {
    loading = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/getTaskFrequencyRows/${idproject}`,
      );
      rows = await response.json();
      const taskFeedbackResponse = await fetchWithSession(
        `/api/damprojects/getTaskFeedbackSetup/${idproject}`,
      );
      if (taskFeedbackResponse.ok) {
        const taskFeedbackData = await taskFeedbackResponse.json();
        taskFeedbackSetup = taskFeedbackData.setup || {};
      }
      frequencies = Object.fromEntries(
        rows.map((row) => [row.id, row.monthly_frequency ?? 100]),
      );
      costCurrency =
        rows.find((row) => row.cost_currency)?.cost_currency || "EUR";
      rowValues = Object.fromEntries(
        rows.map((row) => [
          row.id,
          {
            cost_per_hour: row.cost_per_hour ?? 100,
            cost_currency: row.cost_currency ?? "EUR",
            avg_case_time_seconds:
              row.avg_case_time_seconds ??
              (row.avg_case_time_minutes ? row.avg_case_time_minutes * 60 : ""),
            active_users: row.active_users ?? "",
            standard_case_share: getOptionalPercentageValue(
              row,
              "standard_case_share",
            ),
            rework_share: getOptionalPercentageValue(row, "rework_share"),
            communication_outside_system_share: getOptionalPercentageValue(
              row,
              "communication_outside_system_share",
            ),
            pain_level: row.pain_level ?? "",
            business_criticality: row.business_criticality ?? "",
            input_standardization: row.input_standardization ?? "",
            perceived_value: row.perceived_value ?? "",
            improvement_proposals: row.improvement_proposals ?? "",
            improvement_potentials: row.improvement_potentials ?? [],
          },
        ]),
      );
      fieldSources = Object.fromEntries(
        rows.map((row) => [row.id, normalizeFieldSources(row.field_sources)]),
      );
      systemValues = Object.fromEntries(
        rows.map((row) => [
          row.id,
          {
            active_users: row.system_values?.active_users ?? "",
            avg_case_time_seconds:
              row.system_values?.avg_case_time_seconds ?? "",
          },
        ]),
      );
      surveyValues = Object.fromEntries(
        rows.map((row) => [row.id, row.survey_values || {}]),
      );
      await loadTaskFeedbackResults();
    } finally {
      loading = false;
    }
  }

  function scheduleSave() {
    if (loading || !rows.length) return;
    hasUnsavedChanges = true;
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      saveRows();
    }, 700);
  }

  function buildSavePayload() {
    return {
      project_id: idproject,
      items: rows.map((row) => ({
        subtask_id: row.id,
        l1: row.l1,
        l2: row.l2,
        l3: row.l3,
        l4: row.l4,
        monthly_frequency: toNumber(frequencies[row.id]),
        cost_per_hour: toNumber(getRowValue(row, "cost_per_hour")),
        cost_currency: costCurrency,
        avg_case_time_seconds: toNumber(
          getRowValue(row, "avg_case_time_seconds"),
        ),
        active_users: toNumber(getRowValue(row, "active_users")),
        standard_case_share: toOptionalNumber(
          getRowValue(row, "standard_case_share"),
        ),
        rework_share: toOptionalNumber(getRowValue(row, "rework_share")),
        communication_outside_system_share: toOptionalNumber(
          getRowValue(row, "communication_outside_system_share"),
        ),
        pain_level: getRowValue(row, "pain_level"),
        business_criticality: getRowValue(row, "business_criticality"),
        input_standardization: getRowValue(row, "input_standardization"),
        perceived_value: getRowValue(row, "perceived_value"),
        improvement_proposals: getRowValue(row, "improvement_proposals"),
        improvement_potentials: getImprovementPotentials(row),
        field_sources: fieldSources[row.id] || {},
        survey_values: surveyValues[row.id] || {},
      })),
    };
  }

  async function saveRows() {
    if (!rows.length) return;
    saving = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/saveTaskFrequencyMapping`,
        {
          method: "POST",
          body: JSON.stringify(buildSavePayload()),
        },
      );
      if (!response.ok) {
        throw new Error("Failed to save task frequencies.");
      }
      hasUnsavedChanges = false;
      localStorage.setItem(
        `taskFrequencyUpdated:${idproject}`,
        String(Date.now()),
      );
    } finally {
      saving = false;
    }
  }

  function flushSaveOnUnload() {
    if (!hasUnsavedChanges || !rows.length) return;
    const body = JSON.stringify(buildSavePayload());
    const url = "/api/damprojects/saveTaskFrequencyMapping";
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
    fetchWrapper(url, {
      method: "POST",
      body,
      keepalive: true,
    });
  }

  onMount(loadRows);
  onMount(() => {
    window.addEventListener("beforeunload", flushSaveOnUnload);
  });
  onDestroy(() => {
    window.removeEventListener("beforeunload", flushSaveOnUnload);
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveRows();
    }
  });
</script>

<Header section="KPI" area="Analysis" breadcrumb="Task KPI Setup">
  <!-- Aenderungen werden verzoegert gespeichert, der Status zeigt den echten Speicherstand statt eines Hinweises pro Vorschlag -->
  {#if rows.length}
    <SaveStatus saving={hasUnsavedChanges || saving} />
  {/if}
</Header>

<main class="page_wrapper">
  <div class="main">
    {#if loading}
      <div class="loading_wrapper">
        <Loading withOverlay={false} />
      </div>
    {:else if !rows.length}
      <!-- Zeilen kommen aus den Subtasks im Scope, ohne Scope statt leerer Spaltenkoepfe nur der Hinweis -->
      <SectionWrapper fullscreen={true} seamless={true} emptyState={true}>
        <div slot="emptyState">
          <EmptyStateText
            title="No subtasks in scope yet"
            hint="Task KPIs are set per subtask in this project's scope. Select subtasks in Define Project Scope first."
          >
            <svelte:fragment slot="actions">
              <Button
                kind="secondary"
                size="field"
                text="Go to Define Project Scope"
                on:click={() =>
                  navigate(
                    `${ROUTES.dam.processPresets}?idproject=${idproject}`,
                  )}
              />
            </svelte:fragment>
          </EmptyStateText>
        </div>
      </SectionWrapper>
    {:else}
      <div class="actions">
        <LFilters
          {idproject}
          bind:selectedTeam
          bind:selectedProcess
          bind:selectedSubprocess
          bind:selectedTask
          bind:selectedSubtask
        />
        <div class="top_actions">
          <Button
            text={taskFeedbackSetup?.task_ids?.length
              ? "Edit task feedback"
              : "Request task feedback"}
            kind="secondary"
            size="default"
            on:click={openTaskFeedbackModal}
          />
          <div class="mode_switch">
            <ContentSwitcher
              options={["Hard facts", "Soft facts"]}
              bind:selected={tableModeIndex}
              fitContent
            />
          </div>
        </div>
      </div>
      <div class="task_frequency_table">
        <DataTableBlack
          headers={tableHeaders}
          rows={sortedRows}
          sortable={false}
          slicePage
          bind:page
          bind:pageSize
          pageSizes={[10, 25, 50, 100]}
          totalItems={filteredRows.length}
          searchValue={searchTerm}
          showNewButton={false}
          showPagination={false}
          on:search={(e) => {
            searchTerm = e.detail.value;
            page = 1;
          }}
          on:clearSearch={() => {
            searchTerm = "";
            page = 1;
          }}
        >
          <div slot="right-toolbar" class="currency_filter">
            <!-- <span class="filter_label">Currency</span> -->
            <Select
              items={currencySelectOptions}
              bind:selected={costCurrency}
              color="dark"
              placeholder="Currency"
            />
          </div>
          <svelte:fragment slot="cell-header" let:header>
            <div
              class="header_with_tooltip"
              class:right_header={header.key === "improvement_proposals"}
            >
              <button
                type="button"
                class="header_sort_button"
                on:click={() => setSort(header.key)}
              >
                {header.value}
                {#if sortColumn === header.key}
                  <span
                    class="sort_indicator"
                    class:descending={sortDirection === "desc"}
                    aria-label={sortDirection === "asc"
                      ? "Ascending"
                      : "Descending"}
                  ></span>
                {/if}
              </button>
              {#if header.tooltip}
                <Tooltip>{header.tooltip}</Tooltip>
              {/if}
            </div>
          </svelte:fragment>
          <svelte:fragment slot="cell" let:cell let:row>
            <div
              class:last_cell={cell.key ===
                tableHeaders[tableHeaders.length - 1]?.key}
            >
              {#if cell.key === "l4"}
                <span class="task_name" title={getTaskHierarchyTooltip(row)}>
                  {row.l4}
                </span>
              {:else if cell.key === "improvement_proposals"}
                <div class="proposal_actions">
                  <button
                    type="button"
                    class="proposal_button count"
                    title="View improvement ideas"
                    aria-label="View improvement ideas"
                    on:click={() => openProposalModal(row, "list")}
                  >
                    Ideas: {countImprovementProposals(row, rowValues)}
                  </button>
                  <button
                    type="button"
                    class="proposal_button add"
                    title="Add improvement idea"
                    aria-label="Add improvement idea"
                    on:click={() => openProposalModal(row, "add")}
                  >
                    + Add idea
                  </button>
                </div>
              {:else if editableTextFields.has(cell.key)}
                <div class="value_cell">
                  {#if isQuestionnaireSource(row, cell.key)}
                    <div
                      class="table_source_value"
                      title={getQuestionnaireValueLabel(row, cell.key)}
                    >
                      {getQuestionnaireValueLabel(row, cell.key)}
                    </div>
                  {:else}
                    <input
                      class="table_input"
                      type={cell.key === "cost_per_hour" ? "number" : "text"}
                      min={cell.key === "cost_per_hour" ? "0" : undefined}
                      placeholder="Not set"
                      disabled={!isUserSource(row, cell.key)}
                      value={getEditableInputValue(
                        row,
                        cell.key,
                        rowValues,
                        frequencies,
                      )}
                      on:input={(event) =>
                        setHumanRowValue(
                          row,
                          cell.key,
                          parseEditableInputValue(
                            cell.key,
                            event.currentTarget.value,
                          ),
                        )}
                    />
                  {/if}
                  <select
                    class="source_select"
                    title={sourceTitle(
                      getFieldSource(row, cell.key, fieldSources),
                    )}
                    value={getFieldSource(row, cell.key, fieldSources)}
                    on:change={(event) =>
                      setFieldSource(row, cell.key, event.currentTarget.value)}
                  >
                    {#each sourceOptions as source}
                      <option
                        value={source}
                        disabled={!sourceAvailable(row, cell.key, source)}
                      >
                        {sourceLabel(source)}
                      </option>
                    {/each}
                  </select>
                </div>
              {:else if percentageFields.has(cell.key)}
                <div
                  class="value_cell"
                  class:not_rated_cell={isNotRated(row, cell.key, rowValues)}
                >
                  {#if isQuestionnaireSource(row, cell.key)}
                    <div
                      class="table_source_value"
                      title={getQuestionnaireValueLabel(row, cell.key)}
                    >
                      {getQuestionnaireValueLabel(row, cell.key)}
                    </div>
                  {:else}
                    <select
                      class="table_select"
                      value={String(getDisplayedValue(row, cell.key) ?? "")}
                      disabled={!isUserSource(row, cell.key)}
                      on:change={(event) =>
                        setHumanRowValue(
                          row,
                          cell.key,
                          event.currentTarget.value,
                        )}
                    >
                      {#each percentageOptions as option}
                        <option value={option.value}>{option.label}</option>
                      {/each}
                    </select>
                  {/if}
                  <select
                    class="source_select"
                    title={sourceTitle(
                      getFieldSource(row, cell.key, fieldSources),
                    )}
                    value={getFieldSource(row, cell.key, fieldSources)}
                    on:change={(event) =>
                      setFieldSource(row, cell.key, event.currentTarget.value)}
                  >
                    {#each sourceOptions as source}
                      <option
                        value={source}
                        disabled={!sourceAvailable(row, cell.key, source)}
                      >
                        {sourceLabel(source)}
                      </option>
                    {/each}
                  </select>
                </div>
              {:else if softSelectFields.has(cell.key)}
                <div
                  class="value_cell"
                  class:not_rated_cell={isNotRated(row, cell.key, rowValues)}
                >
                  {#if isQuestionnaireSource(row, cell.key)}
                    <div
                      class="table_source_value"
                      title={getQuestionnaireValueLabel(row, cell.key)}
                    >
                      {getQuestionnaireValueLabel(row, cell.key)}
                    </div>
                  {:else}
                    <select
                      class="table_select"
                      value={getDisplayedValue(row, cell.key)}
                      disabled={!isUserSource(row, cell.key)}
                      on:change={(event) =>
                        setHumanRowValue(
                          row,
                          cell.key,
                          event.currentTarget.value,
                        )}
                    >
                      {#each getLikertSelectOptions(cell.key) as option}
                        <option value={option.value}>{option.label}</option>
                      {/each}
                    </select>
                  {/if}
                  <select
                    class="source_select"
                    title={sourceTitle(
                      getFieldSource(row, cell.key, fieldSources),
                    )}
                    value={getFieldSource(row, cell.key, fieldSources)}
                    on:change={(event) =>
                      setFieldSource(row, cell.key, event.currentTarget.value)}
                  >
                    {#each sourceOptions as source}
                      <option
                        value={source}
                        disabled={!sourceAvailable(row, cell.key, source)}
                      >
                        {sourceLabel(source)}
                      </option>
                    {/each}
                  </select>
                </div>
              {:else}
                {cell.value}
              {/if}
            </div>
          </svelte:fragment>
        </DataTableBlack>
        <div class="table_footer">
          <Pagination
            dark
            bind:page
            bind:pageSize
            totalItems={filteredRows.length}
            pageSizes={[10, 25, 50, 100]}
          />
          <div class="source_legend" aria-label="Value source legend">
            <span class="legend_label">Legend</span>
            <span><b>U</b> User</span>
            <span><b>S</b> System</span>
            <span><b>Q</b> Questionnaire</span>
          </div>
        </div>
      </div>
      {#if proposalModalRow}
        <div class="modal_overlay">
          <div class="proposal_modal" role="dialog" aria-modal="true">
            <button
              type="button"
              class="modal_close"
              aria-label="Close"
              on:click={closeProposalModal}
            >
              x
            </button>
            <div class="modal_header">
              <div>
                <p class="modal_eyebrow">
                  {proposalModalMode === "add"
                    ? "Add improvement idea"
                    : "Improvement ideas"}
                </p>
                <h2>{proposalModalRow.l4}</h2>
              </div>
            </div>

            {#if proposalModalMode === "add"}
              <div class="proposal_editor">
                {#each modalProposalEntries as { proposal, index }, visibleIndex}
                  <div class="proposal_card">
                    <div class="proposal_index">{visibleIndex + 1}</div>
                    <div class="proposal_body">
                      <div class="field_label_row first_label_row">
                        <span class="field_label first_label">Description</span>
                        {#if !String(proposal.text || "").trim()}
                          <span class="required_tag">Required</span>
                        {/if}
                      </div>
                      <textarea
                        class="proposal_text"
                        use:autogrow
                        rows="3"
                        required
                        placeholder="What should be improved for this task?"
                        value={proposal.text}
                        on:input={(event) =>
                          updateImprovementPotential(proposalModalRow, index, {
                            text: event.currentTarget.value,
                          })}
                      ></textarea>
                      <div class="field_label_row">
                        <span class="field_label">Related topics</span>
                        {#if !(proposal.topics || []).length}
                          <span class="required_tag">Required</span>
                        {/if}
                      </div>
                      <div class="chip_group">
                        {#each improvementTopics as topic}
                          <button
                            type="button"
                            class:active={(proposal.topics || []).includes(
                              topic,
                            )}
                            on:click={() =>
                              toggleImprovementTopic(
                                proposalModalRow,
                                index,
                                topic,
                              )}
                          >
                            {topic}
                          </button>
                        {/each}
                      </div>
                      <div class="estimate_grid">
                        <div>
                          <div class="field_label_row">
                            <span class="field_label">Impact on task time</span>
                            {#if !proposal.timeShare}
                              <span class="required_tag">Required</span>
                            {/if}
                          </div>
                          <div class="segmented_options">
                            {#each timeShareOptions as option}
                              <button
                                type="button"
                                class:active={proposal.timeShare === option}
                                on:click={() =>
                                  updateImprovementPotential(
                                    proposalModalRow,
                                    index,
                                    {
                                      timeShare: option,
                                    },
                                  )}
                              >
                                {option}
                              </button>
                            {/each}
                          </div>
                        </div>
                        <div>
                          <div class="field_label_row">
                            <span class="field_label">How often?</span>
                            {#if !proposal.occurrence}
                              <span class="required_tag">Required</span>
                            {/if}
                          </div>
                          <div class="segmented_options">
                            {#each occurrenceOptions as option}
                              <button
                                type="button"
                                class:active={proposal.occurrence === option}
                                on:click={() =>
                                  updateImprovementPotential(
                                    proposalModalRow,
                                    index,
                                    {
                                      occurrence: option,
                                    },
                                  )}
                              >
                                {option}
                              </button>
                            {/each}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="delete_proposal">
                      <Button
                        size="small"
                        icon={TRASH_ICON}
                        kind="secondary"
                        on:click={() =>
                          requestRemoveImprovementPotential(
                            proposalModalRow,
                            index,
                          )}
                      />
                    </div>
                  </div>
                {/each}
                <div class="proposal_footer">
                  <Button
                    text="Cancel"
                    kind="tertiary"
                    size="field"
                    on:click={closeProposalModal}
                  />
                  <div class="proposal_footer_actions">
                    <span
                      class="add_another_wrapper"
                      class:disabled_add_another={!canAddAnotherProposal}
                      data-tooltip={!canAddAnotherProposal
                        ? "Complete all required fields for the previous idea first."
                        : ""}
                    >
                      <Button
                        text="+ Add another idea"
                        kind="tertiary"
                        size="field"
                        disabled={!canAddAnotherProposal}
                        on:click={() =>
                          addAnotherImprovementPotential(proposalModalRow)}
                      />
                    </span>
                    <Button
                      text="Done"
                      kind="secondary"
                      size="field"
                      on:click={closeProposalModal}
                    />
                  </div>
                </div>
              </div>
            {:else}
              <div class="proposal_overview">
                <div class="proposal_summary">
                  <span
                    >{countImprovementProposalsFrom(modalProposals)} ideas</span
                  >
                  <span
                    >Top topic: {getTopicCountsFrom(modalProposals)[0]?.[0] ||
                      "-"}</span
                  >
                  <span
                    >Topics: {getTopicCountsFrom(modalProposals).length}</span
                  >
                  <span
                    >Avg. impact: {getAverageOption(
                      modalProposals,
                      "timeShare",
                      timeShareScores,
                    )}</span
                  >
                  <span
                    >Avg. frequency: {getAverageOption(
                      modalProposals,
                      "occurrence",
                      occurrenceScores,
                    )}</span
                  >
                </div>
                <div class="feedback_list">
                  {#each modalProposalEntries as { proposal, index }}
                    <div class="feedback_card">
                      <div class="feedback_content">
                        <p>
                          {expandedProposalTexts[proposal.id]
                            ? proposal.text
                            : getPreviewText(proposal.text)}
                          {#if String(proposal.text || "").length > 200}
                            <button
                              type="button"
                              class="read_more_button"
                              on:click={() => toggleProposalText(proposal)}
                            >
                              {expandedProposalTexts[proposal.id]
                                ? "Show less"
                                : "Read more"}
                            </button>
                          {/if}
                        </p>
                        <div class="feedback_meta">
                          {#if proposal.source === "survey"}<span
                              >Employee feedback</span
                            >{/if}
                          {#if proposal.pii_replaced}<span
                              >Personal data anonymized</span
                            >{/if}
                          {#each proposal.topics || [] as topic}
                            <span>{topic}</span>
                          {/each}
                          {#if proposal.timeShare}<span
                              >Impact: {proposal.timeShare}</span
                            >{/if}
                          {#if proposal.occurrence}<span
                              >Frequency: {proposal.occurrence}</span
                            >{/if}
                        </div>
                      </div>
                      <div class="feedback_delete">
                        <Button
                          size="small"
                          kind="secondary"
                          icon={TRASH_ICON}
                          on:click={() =>
                            requestRemoveImprovementPotential(
                              proposalModalRow,
                              index,
                            )}
                        />
                      </div>
                    </div>
                  {:else}
                    <div class="empty_feedback">No improvement ideas yet.</div>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        </div>
      {/if}
    {/if}
  </div>
</main>

<Modal
  bind:open={taskFeedbackModalOpen}
  modalHeading="Employee task feedback"
  subtitle={taskFeedbackStep === 1
    ? "Step 1 of 3 · Select the tasks to include."
    : taskFeedbackStep === 2
      ? "Step 2 of 3 · Select the feedback KPIs."
      : "Step 3 of 3 · Configure the survey delivery."}
  actions
  width={720}
  primaryButtonText={taskFeedbackStep === 1
    ? "Continue"
    : taskFeedbackStep === 2
      ? "Continue"
      : savingTaskFeedback
        ? "Saving..."
        : "Save feedback draft"}
  primaryButtonValidations={savingTaskFeedback}
  primaryButtonAction={taskFeedbackStep === 1
    ? continueTaskFeedbackSetup
    : taskFeedbackStep === 2
      ? continueTaskFeedbackDelivery
      : saveTaskFeedbackSetup}
  secondaryButtonText={taskFeedbackStep === 1 ? "Cancel" : "Back"}
  secondaryButtonAction={() => {
    if (taskFeedbackStep === 1) taskFeedbackModalOpen = false;
    else {
      taskFeedbackStep -= 1;
      taskFeedbackError = "";
    }
  }}
>
  {#if taskFeedbackStep === 1}
    <div class="feedback_setup">
      <label class="feedback_field">
        <span>Feedback name</span>
        <input bind:value={taskFeedbackName} maxlength="160" />
      </label>
      <div class="feedback_section_heading">
        <div>
          <strong>Select tasks</strong>
          <p>
            Participants will only be asked about selected tasks assigned to
            their teams.
          </p>
        </div>
        <Button
          text="Select visible"
          kind="tertiary"
          size="field"
          on:click={selectVisibleFeedbackTasks}
        />
      </div>
      <div class="feedback_task_list">
        {#each sortedRows as row}
          <label
            class="feedback_task"
            class:selected={selectedFeedbackTaskIds.includes(String(row.id))}
          >
            <input
              type="checkbox"
              checked={selectedFeedbackTaskIds.includes(String(row.id))}
              on:change={() => toggleFeedbackTask(row.id)}
            />
            <span>
              <strong>{row.l4}</strong>
              <small
                >{[row.l1, row.l2, row.l3].filter(Boolean).join(" · ") ||
                  "No hierarchy assigned"}</small
              >
            </span>
            {#if row.teams?.length}
              <em>{row.teams.join(", ")}</em>
            {/if}
          </label>
        {:else}
          <p class="feedback_empty">No tasks match the current filters.</p>
        {/each}
      </div>
      <p class="feedback_count">
        {selectedFeedbackTaskIds.length} task{selectedFeedbackTaskIds.length ===
        1
          ? ""
          : "s"} selected
      </p>
    </div>
  {:else if taskFeedbackStep === 2}
    <div class="feedback_setup">
      <div class="feedback_recipient_summary">
        <strong
          >{selectedFeedbackTaskIds.length} selected task{selectedFeedbackTaskIds.length ===
          1
            ? ""
            : "s"}</strong
        >
        <span>
          {feedbackTeams.length
            ? `Teams addressed: ${feedbackTeams.join(", ")}`
            : "No team assignment found for the selected tasks."}
        </span>
      </div>
      <div class="feedback_section_heading">
        <div>
          <strong>Feedback questions</strong>
          <p>Select the KPIs to include. There is no limit.</p>
        </div>
        <span class="feedback_count"
          >{selectedFeedbackKpis.length} selected</span
        >
      </div>
      {#each taskFeedbackKpiGroups as group}
        <section class="feedback_kpi_group">
          <div class="feedback_kpi_group_heading">
            <strong>{group.title}</strong>
            <span>{group.description}</span>
          </div>
          <div class="feedback_kpi_grid">
            {#each group.kpis as kpi}
              <button
                type="button"
                class:selected={selectedFeedbackKpis.includes(kpi.id)}
                on:click={() => toggleFeedbackKpi(kpi.id)}
              >
                <span class="feedback_kpi_check"
                  >{selectedFeedbackKpis.includes(kpi.id) ? "✓" : ""}</span
                >
                <span
                  ><strong>{kpi.title}</strong><small>{kpi.description}</small
                  ></span
                >
              </button>
            {/each}
          </div>
        </section>
      {/each}
      <label class="feedback_comment_option">
        <input type="checkbox" bind:checked={feedbackCommentEnabled} />
        <span>
          <strong>Improvement ideas</strong>
          <small
            >Allow employees to describe and classify task-specific ideas.</small
          >
        </span>
      </label>
      <p class="feedback_note">
        Each employee can skip a task they do not know well enough. Results are
        intended to be aggregated at task level.
      </p>
    </div>
  {:else}
    <div class="feedback_setup">
      <div class="feedback_recipient_summary">
        <strong>Recipient scope is set automatically</strong>
        <span
          >{feedbackTeams.length
            ? `The request will later go to the teams assigned to the selected tasks: ${feedbackTeams.join(", ")}.`
            : "The task assignment determines the recipient teams automatically."}</span
        >
      </div>
      <div class="feedback_section_heading">
        <div>
          <strong>Survey delivery</strong>
          <p>Reuse the same schedule and email settings as Potential Scout.</p>
        </div>
      </div>
      <div class="feedback_delivery_grid">
        <label class="feedback_field">
          <span>Start of survey</span>
          <input type="date" bind:value={taskFeedbackSettings.start_date} />
        </label>
        <label class="feedback_field">
          <span>End of survey</span>
          <input type="date" bind:value={taskFeedbackSettings.end_date} />
        </label>
        <label class="feedback_field">
          <span>Survey language</span>
          <select bind:value={taskFeedbackSettings.language}>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
            <option value="es">Español</option>
            <option value="it">Italiano</option>
          </select>
        </label>
      </div>
      <div class="feedback_delivery_section">
        <strong>Reminders</strong>
        <div class="feedback_reminders">
          <label>
            <input type="checkbox" bind:checked={enableFirstFeedbackReminder} />
            <span>First reminder</span>
            <div class="feedback_reminder_timing">
              <input
                type="number"
                min="0"
                max="99"
                disabled={!enableFirstFeedbackReminder}
                bind:value={taskFeedbackSettings.reminder_days}
              />
              <small>days before deadline</small>
            </div>
          </label>
          <label>
            <input
              type="checkbox"
              bind:checked={enableSecondFeedbackReminder}
            />
            <span>Second reminder</span>
            <div class="feedback_reminder_timing">
              <input
                type="number"
                min="0"
                max="99"
                disabled={!enableSecondFeedbackReminder}
                bind:value={taskFeedbackSettings.reminder2_days}
              />
              <small>days before deadline</small>
            </div>
          </label>
        </div>
      </div>
      <div class="feedback_delivery_section">
        <label class="feedback_contact_toggle">
          <input type="checkbox" bind:checked={includeFeedbackContact} />
          <span>
            <strong>Add a contact person</strong>
            <small
              >Show a contact person in the invitation and reminder emails.</small
            >
          </span>
        </label>
        {#if includeFeedbackContact}
          <div class="feedback_delivery_grid">
            <label class="feedback_field">
              <span>Contact person name</span>
              <input
                bind:value={taskFeedbackSettings.contact_person}
                placeholder="Add contact person name"
              />
            </label>
            <label class="feedback_field">
              <span>Contact person email</span>
              <input
                type="email"
                bind:value={taskFeedbackSettings.contact_person_email}
                placeholder="Add contact person email"
              />
            </label>
          </div>
        {/if}
      </div>
      <label class="feedback_field feedback_signature_field">
        <span>Email signature</span>
        <textarea
          rows="3"
          bind:value={taskFeedbackSettings.email_signature}
          placeholder="Add email signature"
        ></textarea>
      </label>
      <p class="feedback_note">
        <strong>Safe by default:</strong> Save feedback draft never sends an email.
        Test invitations can currently only be sent to j.wanner@paxray.com.
      </p>
    </div>
  {/if}
  {#if taskFeedbackError}<p class="feedback_error">{taskFeedbackError}</p>{/if}
  {#if taskFeedbackNotice}<p class="feedback_success">
      {taskFeedbackNotice}
    </p>{/if}
  <svelte:fragment slot="left-buttons">
    {#if taskFeedbackStep === 3}
      <Button
        text="Preview survey"
        kind="tertiary"
        size="field"
        on:click={openTaskFeedbackPreview}
      />
      <Button
        text="Send test invitation"
        kind="tertiary"
        size="field"
        disabled={savingTaskFeedback || sendingTaskFeedback}
        on:click={() => (taskFeedbackSendModalOpen = true)}
      />
    {/if}
  </svelte:fragment>
</Modal>

<Modal
  bind:open={taskFeedbackPreviewOpen}
  modalHeading="Employee survey preview"
  subtitle="This is how an employee will complete the selected task feedback."
  actions
  width={640}
  primaryButtonText={previewTaskIndex >= selectedFeedbackRows.length - 1
    ? "Finish preview"
    : "Next task"}
  primaryButtonAction={nextPreviewTask}
  secondaryButtonText="Close"
  secondaryButtonAction={() => (taskFeedbackPreviewOpen = false)}
>
  {#if previewTask}
    <div class="survey_preview">
      <div class="survey_preview_progress">
        <span>Task {previewTaskIndex + 1} of {selectedFeedbackRows.length}</span
        >
        <span>Estimated time: under 1 minute</span>
      </div>
      <div class="survey_preview_task">
        <span class="survey_preview_eyebrow">Please rate this task</span>
        <h3>{previewTask.l4}</h3>
        <p>
          {[previewTask.l1, previewTask.l2, previewTask.l3]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div class="survey_preview_knowledge">
        <strong>Do you know this task well enough to assess it?</strong>
        <div>
          <button
            type="button"
            class:active={!previewSkipped}
            on:click={() => {
              previewSkipped = false;
              previewError = "";
            }}
          >
            Yes, I can assess it
          </button>
          <button
            type="button"
            class:active={previewSkipped}
            on:click={() => {
              previewSkipped = true;
              previewError = "";
            }}
          >
            Skip this task
          </button>
        </div>
      </div>
      {#if previewSkipped}
        <p class="survey_preview_skip">
          No feedback is required for this task. Continue to the next one.
        </p>
      {:else}
        <div class="survey_preview_questions">
          {#each selectedFeedbackKpis as kpiId}
            {@const kpi = getTaskFeedbackKpi(kpiId)}
            {#if kpi}
              <div class="survey_preview_question">
                <strong>{kpi.title}</strong>
                <span>{kpi.description}</span>
                <div class="survey_preview_scale">
                  {#each getFeedbackOptions(kpiId) as option}
                    <button
                      type="button"
                      class:active={previewValues[kpiId] === option.value}
                      on:click={() => {
                        previewValues = {
                          ...previewValues,
                          [kpiId]: option.value,
                        };
                        previewError = "";
                      }}>{option.label}</button
                    >
                  {/each}
                </div>
              </div>
            {/if}
          {/each}
          {#if feedbackCommentEnabled}
            <section class="survey_preview_ideas">
              <div class="survey_preview_ideas_heading">
                <strong
                  >What would you improve? <em class="optional">Optional</em
                  ></strong
                >
                <span>Describe one idea at a time and classify it briefly.</span
                >
              </div>
              {#each previewIdeas as idea, index}
                <article class="survey_preview_idea_card">
                  <header>
                    <span>{index + 1}</span>
                    <strong>Improvement idea</strong>
                    <button
                      type="button"
                      aria-label="Remove improvement idea"
                      title="Remove improvement idea"
                      on:click={() => removePreviewIdea(index)}>×</button
                    >
                  </header>
                  <label class="survey_preview_idea_description">
                    <strong>
                      Description
                      <em
                        class:optional={!isPreviewIdeaStarted(idea)}
                        class:complete={Boolean(idea.text?.trim())}
                        >{isPreviewIdeaStarted(idea)
                          ? "Required"
                          : "Optional"}</em
                      >
                    </strong>
                    <textarea
                      rows="3"
                      value={idea.text}
                      placeholder="Describe the improvement idea..."
                      on:input={(event) =>
                        updatePreviewIdea(index, {
                          text: event.currentTarget.value,
                        })}
                    ></textarea>
                  </label>
                  <div class="survey_preview_idea_field">
                    <strong>
                      Related topics
                      <em
                        class:optional={!isPreviewIdeaStarted(idea)}
                        class:complete={idea.topics.length}
                        >{isPreviewIdeaStarted(idea)
                          ? "Required"
                          : "Optional"}</em
                      >
                    </strong>
                    <div class="survey_preview_choice_buttons">
                      {#each surveyImprovementTopics as topic}
                        <button
                          type="button"
                          class:selected={idea.topics.includes(topic)}
                          on:click={() => togglePreviewIdeaTopic(index, topic)}
                          >{topic}</button
                        >
                      {/each}
                    </div>
                  </div>
                  <div class="survey_preview_idea_ratings">
                    <div class="survey_preview_idea_field">
                      <strong>
                        Impact on task time
                        <em
                          class:optional={!isPreviewIdeaStarted(idea)}
                          class:complete={idea.timeShare}
                          >{isPreviewIdeaStarted(idea)
                            ? "Required"
                            : "Optional"}</em
                        >
                      </strong>
                      <div class="survey_preview_choice_buttons">
                        {#each timeShareOptions as option}
                          <button
                            type="button"
                            class:selected={idea.timeShare === option}
                            on:click={() =>
                              togglePreviewIdeaValue(
                                index,
                                "timeShare",
                                option,
                              )}>{option}</button
                          >
                        {/each}
                      </div>
                    </div>
                    <div class="survey_preview_idea_field">
                      <strong>
                        How often does it occur?
                        <em
                          class:optional={!isPreviewIdeaStarted(idea)}
                          class:complete={idea.occurrence}
                          >{isPreviewIdeaStarted(idea)
                            ? "Required"
                            : "Optional"}</em
                        >
                      </strong>
                      <div class="survey_preview_choice_buttons">
                        {#each occurrenceOptions as option}
                          <button
                            type="button"
                            class:selected={idea.occurrence === option}
                            on:click={() =>
                              togglePreviewIdeaValue(
                                index,
                                "occurrence",
                                option,
                              )}>{option}</button
                          >
                        {/each}
                      </div>
                    </div>
                  </div>
                </article>
              {/each}
              <button
                type="button"
                class="survey_preview_add_idea"
                disabled={previewIdeas.length > 0 &&
                  !isPreviewIdeaComplete(previewIdeas[previewIdeas.length - 1])}
                on:click={addPreviewIdea}>+ Add another idea</button
              >
            </section>
          {/if}
        </div>
      {/if}
      {#if previewError}<p class="survey_preview_error">{previewError}</p>{/if}
      <p class="survey_preview_note">Preview only — no answers are saved.</p>
    </div>
  {/if}
</Modal>

<Modal
  bind:open={taskFeedbackSendModalOpen}
  modalHeading="Send test invitation?"
  subtitle="The draft is saved and exactly one invitation email is sent."
  actions
  xs
  width={340}
  primaryButtonText={sendingTaskFeedback
    ? "Sending..."
    : "Send test invitation"}
  primaryButtonValidations={sendingTaskFeedback}
  primaryButtonAction={confirmTaskFeedbackTestSend}
  secondaryButtonText="Cancel"
  secondaryButtonAction={() => (taskFeedbackSendModalOpen = false)}
  on:close={() => (taskFeedbackSendModalOpen = false)}
>
  <div class="send_test_body">
    <div class="send_test_recipient">
      <span>Test recipient</span>
      <strong>j.wanner@paxray.com</strong>
    </div>
    <p>Every other recipient is blocked by the backend.</p>
  </div>
</Modal>

<Modal
  bind:open={deleteProposalModalOpen}
  modalHeading="Delete improvement idea?"
  subtitle="This cannot be undone."
  actions
  xs
  width={340}
  primaryButtonText="Delete"
  primaryButtonAction={confirmRemoveImprovementPotential}
  secondaryButtonText="Cancel"
  secondaryButtonAction={() => {
    deleteProposalModalOpen = false;
    deleteProposalTarget = null;
  }}
  on:close={() => {
    deleteProposalModalOpen = false;
    deleteProposalTarget = null;
  }}
>
  <p class="delete_body">The idea is removed from this task.</p>
</Modal>

<style>
  .page_wrapper {
    padding: 0.75rem 1rem 1rem;
  }

  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    padding: 2rem;
  }

  .actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: flex-end;
    gap: 0.75rem;
  }

  /* .filters {
    display: grid;
    grid-template-columns: repeat(5, minmax(10rem, 1fr));
    gap: 0.5rem;
    min-width: 0;
  }

  .filters :global(.custom_select) {
    width: 100%;
  }

  .filter_item {
    min-width: 0;
  }

  .filter_label {
    display: block;
    color: #6f7681;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1;
    margin-bottom: 0.35rem;
  }

  .filters :global(.bx--form-item) {
    width: 100%;
  }

  .filters :global(.bx--select-input) {
    height: 3rem;
    min-height: 3rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    font-family: var(--font-family-inter);
    font-size: 0.875rem;
  }

  .filters :global(.bx--select),
  .filters :global(.bx--select-input__wrapper) {
    height: auto;
    border-radius: 0.5rem;
  }

  .filters :global(.bx--select-input__wrapper) {
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .filters :global(.bx--select__arrow) {
    right: 1rem;
  } */

  .mode_switch {
    align-self: end;
  }

  .top_actions {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
  }

  .feedback_setup {
    /* Local surfaces, all derived from the platform tokens so the wizard
       matches the rest of the app instead of carrying its own greys. */
    --fb-surface: color-mix(in srgb, var(--pax-smoke) 55%, white);
    --fb-divider: color-mix(in srgb, var(--border-color) 45%, transparent);
    --fb-tint: color-mix(in srgb, var(--pax-orange) 7%, white);
    --fb-muted: color-mix(in srgb, var(--pax-black) 68%, white);
    --fb-focus: color-mix(in srgb, var(--pax-orange) 22%, transparent);
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .feedback_field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .feedback_field input,
  .feedback_field select,
  .feedback_field textarea {
    height: 2.5rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    padding: 0 0.75rem;
    font: inherit;
    font-weight: 400;
    color: var(--pax-black);
    background: var(--pax-white);
    transition:
      border-color 120ms ease,
      box-shadow 120ms ease;
  }

  .feedback_field input:focus,
  .feedback_field select:focus,
  .feedback_field textarea:focus,
  .feedback_reminder_timing input[type="number"]:focus {
    outline: none;
    border-color: var(--pax-orange);
    box-shadow: 0 0 0 2px var(--fb-focus);
  }

  .feedback_field textarea {
    height: auto;
    min-height: 5.5rem;
    resize: vertical;
  }

  .feedback_delivery_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .feedback_delivery_grid .feedback_field:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .feedback_delivery_section {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    padding: 0.75rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    background: var(--fb-surface);
  }

  .feedback_delivery_section > strong,
  .feedback_contact_toggle strong {
    font-size: 0.85rem;
  }

  .feedback_reminders {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }

  .feedback_reminders label,
  .feedback_contact_toggle {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    min-width: 0;
  }

  .feedback_reminders input[type="checkbox"],
  .feedback_contact_toggle input {
    accent-color: var(--pax-orange);
  }

  .feedback_reminder_timing {
    grid-column: 2;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }

  .feedback_reminder_timing input[type="number"] {
    width: 3.25rem;
    height: 2rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    padding: 0 0.4rem;
    font: inherit;
    transition:
      border-color 120ms ease,
      box-shadow 120ms ease;
  }

  .feedback_reminders small,
  .feedback_contact_toggle small {
    color: var(--fb-muted);
    font-size: 0.7rem;
  }

  .feedback_contact_toggle {
    align-items: flex-start;
    width: 100%;
  }

  .feedback_contact_toggle > span {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .feedback_signature_field {
    padding-top: 0.25rem;
  }

  .feedback_section_heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .feedback_section_heading > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  .feedback_section_heading > .feedback_count {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .feedback_section_heading strong,
  .feedback_recipient_summary strong,
  .feedback_comment_option strong {
    display: block;
    font-size: 0.9rem;
  }

  .feedback_section_heading p,
  .feedback_recipient_summary span,
  .feedback_comment_option small,
  .feedback_note,
  .feedback_count {
    margin: 0.2rem 0 0;
    color: var(--fb-muted);
    font-size: 0.75rem;
    line-height: 1.35;
  }

  .feedback_task_list {
    max-height: 19rem;
    overflow: auto;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
  }

  .feedback_task {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.7rem;
    padding: 0.7rem 0.8rem;
    border-bottom: var(--bw) solid var(--fb-divider);
    border-left: 2px solid transparent;
    cursor: pointer;
    transition: background 120ms ease;
  }

  .feedback_task:hover {
    background: var(--fb-surface);
  }

  .feedback_task:last-child {
    border-bottom: 0;
  }

  .feedback_task.selected {
    border-left-color: var(--pax-orange);
    background: var(--fb-tint);
  }

  .feedback_task input,
  .feedback_comment_option input {
    accent-color: var(--pax-orange);
  }

  .feedback_task strong,
  .feedback_task small {
    display: block;
  }

  .feedback_task strong {
    font-size: 0.8rem;
    font-weight: 600;
  }

  .feedback_task small,
  .feedback_task em {
    color: var(--fb-muted);
    font-size: 0.7rem;
    font-style: normal;
  }

  .feedback_task em {
    text-align: right;
  }

  .feedback_empty {
    padding: 1rem;
    margin: 0;
    color: var(--fb-muted);
    font-size: 0.8rem;
  }

  .feedback_recipient_summary {
    padding: 0.75rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    background: var(--fb-surface);
  }

  .feedback_kpi_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  /* a lone card in the last row fills the width, as in .feedback_delivery_grid */
  .feedback_kpi_grid button:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .feedback_kpi_group {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.75rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    background: var(--fb-surface);
  }

  .feedback_kpi_group_heading strong,
  .feedback_kpi_group_heading span {
    display: block;
  }

  .feedback_kpi_group_heading strong {
    font-size: 0.8rem;
  }

  .feedback_kpi_group_heading span {
    margin-top: 0.15rem;
    color: var(--fb-muted);
    font-size: 0.7rem;
  }

  .feedback_kpi_grid button {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.6rem 0.65rem;
    text-align: left;
    color: var(--pax-black);
    background: var(--pax-white);
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    cursor: pointer;
    transition:
      border-color 120ms ease,
      background 120ms ease;
  }

  .feedback_kpi_grid button:hover:not(:disabled) {
    border-color: var(--pax-orange);
    background: var(--fb-surface);
  }

  .feedback_kpi_grid button.selected {
    border-color: var(--pax-orange);
    background: var(--fb-tint);
  }

  .feedback_kpi_grid button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .feedback_kpi_grid strong,
  .feedback_kpi_grid small {
    display: block;
  }

  .feedback_kpi_grid strong {
    font-size: 0.8rem;
  }

  .feedback_kpi_grid small {
    margin-top: 0.1rem;
    color: var(--fb-muted);
    font-size: 0.7rem;
    line-height: 1.3;
  }

  .feedback_kpi_check {
    flex: 0 0 1rem;
    width: 1rem;
    height: 1rem;
    border: var(--bw) solid var(--border-color);
    border-radius: 0.25rem;
    color: var(--pax-white);
    background: var(--pax-white);
    font-size: 0.75rem;
    line-height: 0.9rem;
    text-align: center;
  }

  .feedback_kpi_grid button.selected .feedback_kpi_check {
    border-color: var(--pax-orange);
    background: var(--pax-orange);
  }

  .feedback_setup .feedback_comment_option {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: flex-start;
    gap: 0.6rem;
    width: 100%;
    padding: 0.75rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    cursor: pointer;
    transition: border-color 120ms ease;
  }

  .feedback_setup .feedback_comment_option:hover {
    border-color: var(--pax-orange);
  }

  .feedback_comment_option input {
    margin: 0.2rem 0 0;
  }

  .feedback_comment_option > span {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
  }

  .feedback_error {
    margin: 1rem 0 0;
    color: #c0392b;
    font-size: 0.8rem;
  }

  .feedback_success {
    margin: 0.75rem 0 0;
    color: #198038;
    font-size: 0.75rem;
  }

  .survey_preview {
    /* Same local palette as the setup wizard, derived from the platform tokens. */
    --sp-surface: color-mix(in srgb, var(--pax-smoke) 55%, white);
    --sp-muted: color-mix(in srgb, var(--pax-black) 68%, white);
    --sp-focus: color-mix(in srgb, var(--pax-orange) 22%, transparent);
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .survey_preview_progress {
    display: flex;
    justify-content: space-between;
    color: var(--sp-muted);
    font-size: 0.75rem;
  }

  .survey_preview_task {
    padding: 1rem;
    border-radius: var(--br);
    background: var(--sp-surface);
  }

  .survey_preview_eyebrow {
    color: var(--sp-muted);
    font-size: 0.7rem;
    text-transform: uppercase;
  }

  .survey_preview_task h3 {
    margin: 0.35rem 0 0;
    font-size: 1rem;
  }

  .survey_preview_task p,
  .survey_preview_question > span,
  .survey_preview_note,
  .survey_preview_skip {
    margin: 0.25rem 0 0;
    color: var(--sp-muted);
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .survey_preview_knowledge {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .survey_preview_knowledge strong,
  .survey_preview_question strong {
    font-size: 0.85rem;
  }

  .survey_preview_knowledge > div {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .survey_preview_knowledge button,
  .survey_preview_scale button {
    min-height: 2.25rem;
    padding: 0.35rem 0.7rem;
    color: var(--pax-black);
    background: var(--pax-white);
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    cursor: pointer;
    transition:
      border-color 120ms ease,
      background 120ms ease;
  }

  .survey_preview_scale button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 3.15rem;
    padding: 0.45rem;
    font-size: 0.7rem;
    line-height: 1.2;
    text-align: center;
  }

  .survey_preview_scale {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.4rem;
  }

  .survey_preview_knowledge button:hover,
  .survey_preview_scale button:hover {
    border-color: var(--pax-orange);
    background: var(--sp-surface);
  }

  .survey_preview_knowledge button:focus-visible,
  .survey_preview_scale button:focus-visible,
  .survey_preview_idea_description textarea:focus {
    outline: none;
    border-color: var(--pax-orange);
    box-shadow: 0 0 0 2px var(--sp-focus);
  }

  .survey_preview_knowledge button.active,
  .survey_preview_scale button.active {
    color: var(--pax-white);
    background: var(--pax-orange);
    border-color: var(--pax-orange);
  }

  .survey_preview_questions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .survey_preview_question {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .survey_preview_ideas {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .survey_preview_ideas_heading {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .survey_preview_ideas_heading strong,
  .survey_preview_idea_card > header strong,
  .survey_preview_idea_field > strong,
  .survey_preview_idea_description > strong {
    font-size: 0.7rem;
  }

  .survey_preview_ideas_heading span {
    color: var(--sp-muted);
    font-size: 0.72rem;
  }

  .survey_preview_ideas em,
  .survey_preview_idea_field em,
  .survey_preview_idea_description em {
    padding: 0.25rem 0.55rem;
    border: var(--bw) solid
      color-mix(in srgb, var(--pax-red) 70%, var(--pax-black));
    border-radius: 1rem;
    background: var(--pax-red);
    color: color-mix(in srgb, var(--pax-red) 35%, var(--pax-black));
    font-size: 0.65rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.1;
  }

  .survey_preview_idea_field em.complete,
  .survey_preview_idea_description em.complete {
    border-color: color-mix(in srgb, var(--pax-green) 70%, var(--pax-black));
    background: var(--pax-green);
    color: color-mix(in srgb, var(--pax-green) 35%, var(--pax-black));
  }

  .survey_preview_ideas_heading em.optional,
  .survey_preview_idea_field em.optional,
  .survey_preview_idea_description em.optional {
    border-color: transparent;
    color: var(--sp-muted);
    background: transparent;
    padding-right: 0;
  }

  .survey_preview_idea_card {
    width: 100%;
    box-sizing: border-box;
    padding: 1rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    background: var(--pax-white);
  }

  .survey_preview_idea_card > header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .survey_preview_idea_card > header > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: var(--br);
    background: var(--sp-surface);
    font-weight: 700;
  }

  .survey_preview_idea_card > header > button {
    width: 1.75rem;
    height: 1.75rem;
    margin-left: auto;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    color: var(--sp-muted);
    background: transparent;
    font-size: 0.9rem;
    line-height: 1;
    cursor: pointer;
    transition:
      color 120ms ease,
      border-color 120ms ease,
      background 120ms ease;
  }

  .survey_preview_idea_card > header > button:hover {
    border-color: var(--pax-danger);
    color: var(--pax-danger);
    background: color-mix(in srgb, var(--pax-danger) 8%, white);
  }

  .survey_preview_idea_description,
  .survey_preview_idea_field {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.45rem;
    margin-top: 0.85rem;
    text-align: left;
  }

  .survey_preview_idea_description textarea {
    width: 100%;
    resize: vertical;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    padding: 0.6rem;
    font: inherit;
    font-size: 0.75rem;
  }

  .survey_preview_choice_buttons {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 0.45rem;
    width: 100%;
  }

  .survey_preview_choice_buttons button,
  .survey_preview_add_idea {
    max-width: 100%;
    padding: 0.5rem 0.7rem;
    overflow: hidden;
    border: 1px solid #cfd4da;
    border-radius: var(--br);
    color: var(--pax-black);
    background: #fff;
    font-size: 0.68rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .survey_preview_choice_buttons button.selected {
    border-color: var(--pax-black);
    color: #fff;
    background: var(--pax-black);
  }

  .survey_preview_idea_ratings {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .survey_preview_add_idea {
    align-self: flex-start;
  }

  .survey_preview_add_idea:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .survey_preview_error {
    margin: 0;
    color: #9f3f48;
    font-size: 0.75rem;
  }

  .currency_filter {
    min-width: 0;
  }

  .currency_filter :global(.custom_select) {
    width: 100%;
  }

  .currency_filter :global(.bx--form-item) {
    width: 100%;
  }

  .currency_filter :global(.bx--select-input) {
    height: 3rem;
    min-height: 3rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    font-family: var(--font-family-inter);
    font-size: 0.875rem;
  }

  .currency_filter :global(.bx--select),
  .currency_filter :global(.bx--select-input__wrapper) {
    height: auto;
    border-radius: 0.5rem;
  }

  .currency_filter :global(.bx--select-input__wrapper) {
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .currency_filter :global(.bx--select__arrow) {
    right: 1rem;
  }

  .table_footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    padding-top: 0.75rem;
  }

  .table_footer :global(.bx--pagination) {
    flex: 1 1 auto;
    margin: 0;
  }

  .source_legend {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    min-height: 2.5rem;
    padding: 0 0.85rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: #52525a;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .legend_label {
    color: #6f7681;
    font-weight: 700;
  }

  .source_legend span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .source_legend b {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.15rem;
    height: 1.15rem;
    margin-right: 0.2rem;
    border: 1px solid #d7dbe0;
    border-radius: var(--br);
    color: var(--pax-black);
    font-size: 0.7rem;
  }

  .header_with_tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    max-width: 100%;
  }

  .header_sort_button {
    min-width: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    font: inherit;
    line-height: 1.15;
    text-align: left;
    cursor: pointer;
  }

  .header_with_tooltip.right_header {
    justify-content: flex-end;
    width: 100%;
  }

  .header_with_tooltip.right_header .header_sort_button {
    text-align: right;
  }

  .header_sort_button:hover,
  .header_sort_button:focus {
    background: transparent;
  }

  .sort_indicator {
    display: inline-block;
    width: 0.38rem;
    height: 0.38rem;
    margin-left: 0.25rem;
    border: solid currentColor;
    border-width: 0 1.5px 1.5px 0;
    transform: translateY(0.1rem) rotate(-135deg);
  }

  .sort_indicator.descending {
    transform: translateY(-0.1rem) rotate(45deg);
  }

  .task_name {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
  }

  .last_cell {
    display: flex;
    justify-content: flex-end;
    text-align: right;
    width: 100%;
  }

  .last_cell .value_cell {
    width: min(100%, 11.5rem);
  }

  .table_input,
  .table_select {
    width: 100%;
    min-width: 0;
    height: 2rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.25rem;
    background: #fff;
    color: var(--pax-black);
    font: inherit;
    padding: 0 0.35rem;
  }

  .table_input {
    text-align: center;
  }

  .value_cell {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 1.75rem;
    grid-template-rows: 2rem;
    min-width: 0;
    overflow: hidden;
    border: 1px solid #d7dbe0;
    border-radius: 0.3rem;
    background: #fff;
  }

  .not_rated_cell {
    border-color: #e6a7ad;
    background: rgba(230, 167, 173, 0.22);
  }

  .not_rated_cell .table_select,
  .not_rated_cell .source_select {
    background: transparent;
  }

  .not_rated_cell .table_select {
    color: #9f3f48;
    font-weight: 700;
  }

  .value_cell .table_input,
  .value_cell .table_select {
    height: 2rem;
    border: none;
    border-radius: 0;
  }

  .table_source_value {
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 0 0.45rem;
    overflow: hidden;
    color: #344054;
    font-size: 0.7rem;
    line-height: 1.2;
  }

  .not_rated_cell .table_source_value {
    color: #9f3f48;
    font-weight: 700;
  }

  .source_select {
    width: 1.75rem;
    min-width: 0;
    height: 2rem;
    border: none;
    border-left: 1px solid var(--pax-black);
    border-radius: 0;
    background: #fff;
    color: var(--pax-black);
    font-size: 0.8rem;
    font-weight: 800;
    line-height: 1;
    padding: 0 0.2rem;
    text-align: center;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
  }

  .source_select option:disabled {
    color: #b8bcc2;
  }

  .table_select {
    cursor: pointer;
  }

  .table_input:disabled,
  .table_select:disabled {
    background: var(--sp-surface);
    color: var(--sp-muted);
    cursor: not-allowed;
  }

  .proposal_actions {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    justify-content: flex-end;
    gap: 0.35rem;
    width: fit-content;
    margin-left: auto;
  }

  .proposal_button {
    height: 2rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.25rem;
    background: #fff;
    color: var(--pax-black);
    font-size: 0.85rem;
    font-weight: 400;
    cursor: pointer;
  }

  .proposal_button.count {
    min-width: 4.4rem;
    padding: 0 0.55rem;
  }

  .proposal_button.add {
    min-width: 5rem;
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
    padding: 0 0.55rem;
  }

  .proposal_button:hover {
    border-color: var(--pax-black);
  }

  .modal_overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
  }

  .proposal_modal {
    position: relative;
    width: min(76rem, calc(100vw - 2rem));
    max-height: 82vh;
    min-height: 22rem;
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: var(--br);
    background: #fff;
    box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.24);
  }

  .modal_close {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    width: 2rem;
    height: 2rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.25rem;
    background: #fff;
    color: var(--pax-black);
    font-size: 0.95rem;
    font-weight: 800;
    cursor: pointer;
  }

  .modal_close:hover {
    border-color: var(--pax-black);
  }

  .modal_header {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    padding: 1.5rem 2rem 1rem;
    border-bottom: 1px solid #edf0f3;
  }

  .modal_eyebrow {
    display: inline-flex;
    margin: 0 0 0.55rem;
    padding: 0.25rem 0.55rem;
    border-radius: 0.25rem;
    background: #f4f4f4;
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .modal_header h2 {
    margin: 0;
    color: var(--pax-black);
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .proposal_editor,
  .proposal_overview {
    padding: 1rem 2rem 2rem;
  }

  .proposal_card {
    display: grid;
    grid-template-columns: 2.5rem minmax(0, 1fr) 2.5rem;
    align-items: start;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    border: 1px solid #d7dbe0;
    border-radius: var(--br);
    background: #fff;
  }

  .proposal_index {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--br);
    background: #f4f4f4;
    color: var(--pax-black);
    font-size: 1.1rem;
    font-weight: 800;
  }

  .field_label {
    display: block;
    margin: 0.85rem 0 0.4rem;
    color: #52525a;
    font-size: 0.8rem;
    font-weight: 700;
  }

  .field_label_row {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0.85rem 0 0.4rem;
  }

  .field_label_row .field_label {
    margin: 0;
  }

  .first_label_row {
    margin-top: 0;
  }

  .proposal_text {
    width: 100%;
    min-height: 5.4rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.25rem;
    color: var(--pax-black);
    font: inherit;
    overflow: hidden;
    padding: 0.65rem 0.75rem;
    resize: none;
  }

  .required_tag {
    display: inline-flex;
    width: fit-content;
    padding: 0.2rem 0.45rem;
    border-radius: 999px;
    background: #e6a7ad;
    color: #8f2430;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
  }


  .chip_group,
  .segmented_options {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
  }

  .chip_group button,
  .segmented_options button {
    height: 2rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.25rem;
    background: #fff;
    color: #30333a;
    font-size: 0.82rem;
    font-weight: 700;
    padding: 0 0.75rem;
    cursor: pointer;
  }

  .chip_group button.active,
  .segmented_options button.active {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: #fff;
  }

  .estimate_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
    margin-top: 0.7rem;
  }

  .delete_proposal {
    width: 2.5rem;
  }

  .delete_proposal :global(.button_wrapper) {
    width: 2.5rem;
    height: 2.5rem;
  }

  .proposal_footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  .proposal_footer_actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .proposal_footer :global(.button-outline) {
    border-color: var(--pax-black) !important;
  }

  .add_another_wrapper {
    position: relative;
    display: inline-flex;
  }

  .add_another_wrapper.disabled_add_another {
    cursor: not-allowed;
  }

  .add_another_wrapper.disabled_add_another:hover::after {
    position: absolute;
    right: 0;
    bottom: calc(100% + 0.4rem);
    z-index: 10;
    width: max-content;
    max-width: 18rem;
    padding: 0.45rem 0.6rem;
    border-radius: 0.25rem;
    background: var(--pax-black);
    color: var(--pax-white);
    content: attr(data-tooltip);
    font-size: 0.75rem;
    line-height: 1.25;
    text-align: left;
    white-space: normal;
    pointer-events: none;
  }

  .add_another_wrapper :global(.button-disable) {
    cursor: not-allowed;
  }

  .proposal_summary {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .proposal_summary span {
    padding: 0.35rem 0.65rem;
    border: 1px solid var(--pax-black);
    border-radius: 999px;
    background: #fff;
    color: #30333a;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .feedback_list {
    display: flex;
    max-height: min(56vh, 40rem);
    flex-direction: column;
    gap: 0.75rem;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .feedback_card,
  .empty_feedback {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    max-width: 100%;
    min-width: 0;
    padding: 1rem;
    border: 1px solid #d7dbe0;
    border-radius: var(--br);
    background: #fff;
  }

  .feedback_card {
    border-color: var(--pax-black);
  }

  .feedback_content {
    flex: 1 1 auto;
    min-width: 0;
  }

  .feedback_card p {
    margin: 0 0 0.75rem;
    color: var(--pax-black);
    font-size: 0.95rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  .read_more_button {
    margin-left: 0.25rem;
    border: 0;
    background: transparent;
    color: var(--pax-black);
    cursor: pointer;
    font: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    padding: 0;
    text-decoration: underline;
  }

  .feedback_meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .feedback_meta span {
    padding: 0.18rem 0.48rem;
    border: 1px solid var(--pax-black);
    border-radius: 999px;
    background: #fff;
    color: #30333a;
    font-size: 0.68rem;
    font-weight: 700;
  }

  .feedback_delete {
    display: flex;
    flex: 0 0 2.5rem;
    justify-content: center;
  }

  .send_test_body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .send_test_recipient {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.75rem;
    border: var(--bw) solid var(--border-color);
    border-radius: var(--br);
    background: color-mix(in srgb, var(--pax-smoke) 55%, white);
  }

  .send_test_recipient span {
    color: color-mix(in srgb, var(--pax-black) 68%, white);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .send_test_recipient strong {
    font-size: 0.85rem;
  }

  .send_test_body p {
    margin: 0;
    color: color-mix(in srgb, var(--pax-black) 68%, white);
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .delete_body {
    margin: 0;
    color: color-mix(in srgb, var(--pax-black) 68%, white);
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .empty_feedback {
    color: #666b73;
  }
</style>
